/**
 * Campanhas de cupom (ações promocionais de pico).
 *
 * Como funciona:
 *  - Fluxo PADRÃO usa cupons `LOTEPROMO` / `LOTEPROMOASS` no lote promocional.
 *  - Durante uma "campanha" (ex: Plantão FEO), todos os cupons do fluxo padrão
 *    são substituídos pelo cupom da campanha (mesmo cupom para "unica" e
 *    "assinatura", das 6 pós).
 *  - Google Ads (variant "gg") NÃO é afetado — mantém seus próprios cupons.
 *  - Ativação/desativação: painel admin em /admin/campanhas.
 *
 * Persistência:
 *  - A campanha ativa é gravada em `site_config` na chave `checkout.campanha-ativa`.
 *  - A ausência desta chave = fluxo padrão (lote promocional).
 *  - Cada ativação/desativação também gera uma linha em `campaign_activation_log`.
 */

import { getOverride } from "@/lib/site-overrides";

export type CampaignCoupons = {
  padrao?: { unica: string; assinatura: string };
  /** quando presente, a variante Google Ads também troca de cupom */
  gg?: { unica: string; assinatura: string };
};

export type ActiveCampaign = {
  /** slug da campanha (ex: "plantaofeo", "black-friday") — vira parte da tag do lead */
  slug: string;
  /** nome de exibição (ex: "Plantão FEO") */
  nome: string;
  /** cupom Eduzz aplicado (ex: "PLANTAOFEO") — fallback para todas as variantes */
  cupom: string;
  /**
   * Cupons por variante/tipo de compra. Usado pelas ações que têm cupom
   * próprio para o tráfego Google (ex: Semana da Fisio →
   * SEMANADAFISIO / SEMANADAFISIOGG). Ausente = usa `cupom` no fluxo padrão.
   */
  cupons?: CampaignCoupons;
  /**
   * Rota "espelho" opcional. Quando setada, `/pos` renderiza a LP dessa rota
   * (ex: `/plantaofeo`). Se ausente, `/pos` mantém o catálogo padrão com um
   * banner sinalizando a campanha.
   */
  mirrorRoute?: string;
  /** true quando o admin digitou um cupom livre; false para presets do catálogo */
  isCustom: boolean;
  /** ISO timestamp da ativação */
  ativadoEm: string;
};

/** Chave em site_config onde a campanha ativa é gravada. */
export const ACTIVE_CAMPAIGN_KEY = "checkout.campanha-ativa";

/**
 * Presets do catálogo — campanhas recorrentes com página espelho pronta.
 * Cupons livres NÃO precisam estar aqui; o admin digita o nome e pronto.
 */
export const CAMPAIGN_PRESETS: Array<{
  slug: string;
  nome: string;
  cupom: string;
  cupons?: CampaignCoupons;
  mirrorRoute?: string;
  descricao: string;
}> = [
  {
    slug: "plantaofeo",
    nome: "Plantão FEO",
    cupom: "PLANTAOFEO",
    mirrorRoute: "/plantaofeo",
    descricao: "Ação relâmpago com contador diário. /pos passa a exibir a LP /plantaofeo.",
  },
];



/**
 * Aplica o cupom da campanha em uma URL de checkout (substitui o valor de `cupom=`).
 * Se a URL não tiver `cupom=`, retorna a URL original.
 */
export function applyCampaignCoupon(url: string, cupom: string): string {
  return url.replace(/([?&]cupom=)[^&]+/, `$1${cupom}`);
}

/**
 * Lê a campanha ativa do cache client-side de site-overrides.
 * Retorna null se não houver campanha ativa OU se o cache ainda não hidratou.
 */
export function getActiveCampaignClient(): ActiveCampaign | null {
  const v = getOverride<ActiveCampaign>(ACTIVE_CAMPAIGN_KEY);
  if (!v || typeof v !== "object" || !v.cupom || !v.slug) return null;
  return v;
}

/**
 * Cupom que a campanha aplica em uma variante/tipo de compra.
 * Retorna null quando a campanha não altera essa combinação
 * (ex: Plantão FEO não mexe no tráfego Google).
 */
export function campaignCouponFor(
  camp: ActiveCampaign | null,
  variant: "padrao" | "gg",
  tipo: "unica" | "assinatura",
): string | null {
  if (!camp) return null;
  const porVariante = camp.cupons?.[variant]?.[tipo];
  if (porVariante) return porVariante;
  if (variant === "gg") return null;
  return camp.cupom || null;
}
